import { get } from 'radash';

/**
 * Comparator function for sorting strings with Norwegian locale.
 * Uses Intl.Collator with numeric sorting (e.g., "2" < "10").
 *
 * @example
 * names.toSorted(norwegianCompare)
 * keys.toSorted(norwegianCompare)
 */
export const norwegianCompare = new Intl.Collator('nb-NO', { numeric: true }).compare;

/**
 * Primitive types that terminate the PropertyPath recursion.
 * When we hit one of these, we don't recurse deeper into nested properties.
 */
type SortableValue = string | number | boolean | Date | bigint | symbol | null | undefined;

/**
 * Recursive type that generates all valid dot-notation property paths for type T.
 * Provides TypeScript autocompletion for nested properties like 'user.address.city'.
 *
 * Uses `infer S` pattern to avoid TypeScript "excessively deep" recursion errors.
 */
type PropertyPath<T> = {
  [K in keyof T & string]: T[K] extends SortableValue
    ? K
    : T[K] extends unknown[]
      ? K
      : T[K] extends object
        ? PropertyPath<T[K]> extends infer S
          ? K | `${K}.${S & string}`
          : K
        : K;
}[keyof T & string];

/**
 * Sort property with optional '-' prefix for descending order.
 * Examples: 'name', '-createdAt', 'customer.email', '-order.total'
 */
type SortProperty<T> = PropertyPath<T> | `-${PropertyPath<T>}`;

/**
 * Compares two values for sorting.
 *
 * Null, undefined, and empty string values are always sorted last,
 * regardless of sort direction. This matches expected behavior where
 * "missing" data appears at the end of sorted lists.
 */
function compare(aVal: unknown, bVal: unknown, desc: boolean): number {
  const aIsEmpty = aVal == null || aVal === '';
  const bIsEmpty = bVal == null || bVal === '';

  // Empty values always sort last
  if (aIsEmpty && bIsEmpty) return 0;
  if (aIsEmpty) return 1;
  if (bIsEmpty) return -1;

  let cmp: number;

  if (aVal instanceof Date && bVal instanceof Date) {
    cmp = aVal.getTime() - bVal.getTime();
  } else if (typeof aVal === 'number' && typeof bVal === 'number') {
    cmp = aVal - bVal;
  } else {
    // Handles strings, booleans, bigints via String() conversion
    cmp = norwegianCompare(String(aVal), String(bVal));
  }

  return desc ? -cmp : cmp;
}

const STRIP_PREFIX_REGEX = /^-/;

/**
 * Sorts an array of objects by a property path. Returns a new sorted array (immutable).
 *
 * - Prefix property with '-' for descending order
 * - Supports nested properties with dot notation
 * - Null/undefined/empty values always sort last
 * - Uses Norwegian locale for string comparison
 * - Numeric string sorting (e.g., "2" < "10")
 * - TypeScript autocompletion for property paths
 *
 * @example
 * sort(users, 'name')              // ascending by name
 * sort(orders, '-total')           // descending by total
 * sort(items, 'category.name')     // nested property
 * sort(products, 'articleNumber')  // numeric strings sorted correctly
 */
export function sort<T extends object>(data: T[], property?: SortProperty<T>): T[] {
  if (!property) return data;
  const desc = property.startsWith('-');
  const prop = property.replace(STRIP_PREFIX_REGEX, '');

  return data.toSorted((a, b) => {
    const aVal = get(a, prop);
    const bVal = get(b, prop);
    return compare(aVal, bVal, desc);
  });
}
